import { useRealStudents } from "@/hooks/useRealStudents";
import { useRealAttendance } from "@/hooks/useRealAttendance";
import { useRealSchoolSettings } from "@/hooks/useRealSchoolSettings";
import { useLateAttendance } from "@/hooks/useLateAttendance";
import { ClassesAttendanceCard } from "@/components/admin/dashboard/ClassesAttendanceCard";
import { WeeklyAttendanceChart } from "@/components/reports/charts/WeeklyAttendanceChart";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/hooks/auth";
import { LoginForm } from "@/components/auth";
import { NoStudentsState } from "@/components/students/NoStudentsState";
import { DashboardStats } from "@/components/admin/dashboard/DashboardStats";
import { UnifiedLoadingSpinner } from "@/components/loading/UnifiedLoadingSpinner";
import { dateSplitter } from "@/utils/formatting/deteSplitter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartAreaIcon } from "lucide-react";
import UsersChart from "@/components/reports/charts/UsersChart";

const General = () => {
  const { user, loading: authLoading } = useAuth();

  // التحقق من المصادقة أولاً قبل استدعاء hooks أخرى
  if (authLoading) {
    return (
      <div
        className="container mx-auto px-4 py-2 flex justify-center items-center min-h-screen"
        dir="rtl"
      >
        <UnifiedLoadingSpinner size="md" />
      </div>
    );
  }

  // إذا لم يكن المستخدم مصادق عليه، إظهار نموذج تسجيل الدخول
  if (!user) {
    return <LoginForm />;
  }

  return <AuthenticatedIndex />;
};

// مكون منفصل للمحتوى المصادق عليه
const AuthenticatedIndex = () => {
  const { students, fetchingData } = useRealStudents();
  const { useAttendanceByDate } = useRealAttendance();
  const { getTodayLateCount } = useLateAttendance();
  const { settings, loading } = useRealSchoolSettings();
  const [refreshKey, setRefreshKey] = useState(0);
  // const [attendanceRecords, setAttendanceRecords] = useState([]);

  // Get today's date - مؤشر ثابت لليوم
  const today = useMemo(() => dateSplitter(new Date()), []);

const { data: attendanceRecords, isLoading } = useAttendanceByDate(today, 1);
  // Force refresh when attendance records change - بدون إعادة جلب البيانات
  useEffect(() => {
    setRefreshKey((prev) => prev + 1);
  }, [attendanceRecords?.length]); // إزالة المعاملات المسببة للحلقة

  // Calculate today's attendance statistics

  // استخدام useMemo لتحسين الأداء
  const todayAttendance = useMemo(() => {
    const getTodayAttendance = () => {
      const todayRecords = attendanceRecords?.filter(
        (record) => record.date === today
      );
      const todayLateRecords = getTodayLateCount(); // استخدام الدالة الموجودة لحساب المتأخرين

      const presentCount = todayRecords?.filter(
        (record) => record.status === "present"
      ).length;
      const absentCount = todayRecords?.filter(
        (record) =>
          record.status === "absent" || record.status === "absent_with_reason"
      ).length;
      const withReason = todayRecords?.filter(
        (record) => record.status === "absent_with_reason"
      ).length;

      const result = {
        present: presentCount,
        absent: absentCount,
        late: todayLateRecords, // استخدام عدد المتأخرين من late_attendance_records
        total: students.length,
        withReason,
        attendanceRate:
          students.length > 0
            ? Math.round(
                ((presentCount + todayLateRecords) / students.length) * 100
              )
            : 0,
      };

      return result;
    };
    return getTodayAttendance();
  }, [attendanceRecords, getTodayLateCount, students.length, today]);

  const dashboardData = useMemo(
    () => ({
      totalStudents: students.length,
      presentStudents: todayAttendance.present,
      absentStudents: todayAttendance.absent,
      lateStudents: todayAttendance.late,
      withReason: todayAttendance.withReason,
    }),
    [
      students.length,
      todayAttendance.present,
      todayAttendance.absent,
      todayAttendance.late,
      todayAttendance.withReason,
    ]
  );
  const schoolLogo = useMemo(
    () =>
      settings?.school_name && settings.school_name.trim()
        ? settings.school_name.trim().charAt(0)
        : "م",
    [settings?.school_name]
  );

  // If fetching data, show loading spinner
  if (fetchingData || isLoading) {
    return (
      <div
        className="container mx-auto px-4 py-2 flex justify-center items-center min-h-screen"
        dir="rtl"
      >
        <UnifiedLoadingSpinner size="md" />
      </div>
    );
  }

  // If no students, show the no students state
  if (students.length === 0) {
    return <NoStudentsState schoolLogo={schoolLogo} />;
  }

  // Main dashboard view when everything is set up
  return (
    <div
      key={refreshKey}
      className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-emerald-50/20 p-4 lg:p-6"
      dir="rtl"
    >
      <div className="max-w-7xl mx-auto space-y-6 lg:space-y-8">
        {/* Quick Stats Cards */}
        <DashboardStats
          totalStudents={dashboardData.totalStudents}
          presentStudents={dashboardData.presentStudents}
          absentStudents={dashboardData.absentStudents}
          lateStudents={dashboardData.lateStudents}
          withReason={dashboardData.withReason}
        />

        {/* Classes Attendance Status Card */}
        <ClassesAttendanceCard />

        {/* Weekly Attendance Chart */}
        <WeeklyAttendanceChart />

        <Card className="bg-gradient-to-br from-white/95 to-white/80 backdrop-blur-lg shadow-xl border-0 rounded-2xl lg:rounded-3xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-600/10"></div>
          <CardHeader
            className="relative z-10 pb-3 p-3 lg:p-4 text-white rounded-t-2xl lg:rounded-t-3xl"
            style={{ backgroundColor: "#11a7a2" }}
          >
            <CardTitle className="text-base lg:text-lg font-bold text-white text-left flex items-center justify-start gap-2">
              <ChartAreaIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
              <span>إحصائيات المستخدمين</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 lg:p-4 pt-4 relative z-10">
            <UsersChart />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default General;
